import React, { useState, useEffect } from "react";
import { CheckCircle2, ChevronUp, ChevronDown, Settings2, List, RefreshCw, Calendar as CalendarIcon, Edit2 } from "lucide-react";
import { useLedger } from "../context/LedgerContext";

export default function Dashboard({
  userName = "Founder",
  accounts = [],
  bills = [],
  transactions = [],
  paydayConfig = {},
  setIsPaydaySetupOpen,
  setIsNotificationsOpen,
  collapsedPaydays = {},
  toggleCollapse,
  handleBillClick,
  setSelectedEntry,
  isDarkMode,
  formatPaydayDateStr,
  renderHeroShell,
  changeTab,
  isEntrepreneurMode = false
}) {
  const { currencySymbol = "$" } = useLedger();
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [gaugePhase, setGaugePhase] = useState("start");
  
  // Strict Single-Open Accordion State
  const [activePayday, setActivePayday] = useState("init");

  useEffect(() => {
    setIsMounted(true);
    if (typeof window !== "undefined" && "Notification" in window) {
      setIsPushEnabled(Notification.permission === "granted");
    }

    // Ignition Sweep Animation Timers
    const sweepTimer = setTimeout(() => setGaugePhase("sweep"), 100);
    const settleTimer = setTimeout(() => setGaugePhase("settle"), 1200);

    return () => {
      clearTimeout(sweepTimer);
      clearTimeout(settleTimer);
    };
  }, []);

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      return new Date(parts[0], parseInt(parts[1], 10) - 1, parts[2]).getTime();
    }
    return new Date(dateStr).getTime();
  };

  const currentHour = new Date().getHours();
  let greetingStr = `Evening, ${userName}`;
  if (currentHour >= 5 && currentHour < 12) { greetingStr = `Morning, ${userName}`; }
  if (currentHour >= 12 && currentHour < 17) { greetingStr = `Afternoon, ${userName}`; }
  if (currentHour >= 17 && currentHour < 22) { greetingStr = `Evening, ${userName}`; }
  if (currentHour >= 22 || currentHour < 5) { greetingStr = `Up late, ${userName}?`; }

  const todayForMath = new Date();
  const currentMonthName = todayForMath.toLocaleString("en-US", { month: "long" });
  const currentMonthIdx = todayForMath.getMonth(); 
  const currentYearIdx = todayForMath.getFullYear(); 

  const lastDayOfMonth = new Date(currentYearIdx, currentMonthIdx + 1, 0).getDate();

  const todayParts = [todayForMath.getFullYear(), todayForMath.getMonth(), todayForMath.getDate()];
  const todayMidnightMillis = new Date(todayParts[0], todayParts[1], todayParts[2]).getTime();

  // === TOMORROW ENGINE (FOR DUE TOMORROW PILL) ===
  const tomorrowObj = new Date(todayForMath);
  tomorrowObj.setDate(tomorrowObj.getDate() + 1);
  const tomorrowMidnightMillis = new Date(tomorrowObj.getFullYear(), tomorrowObj.getMonth(), tomorrowObj.getDate()).getTime();

  const dueTomorrowTotal = bills.reduce((sum, bill) => {
    if (bill.isPaid || !bill.rawDate) return sum;
    const parts = bill.rawDate.split("-");
    if (parts.length === 3) {
      const bDate = new Date(parts[0], parseInt(parts[1], 10) - 1, parts[2]);
      const bMidnight = new Date(bDate.getFullYear(), bDate.getMonth(), bDate.getDate()).getTime();
      if (bMidnight === tomorrowMidnightMillis) {
        return sum + (Number(bill.amount) || 0);
      }
    }
    return sum;
  }, 0);

  // === PAYDAY INTERVAL RANGE ENGINE SYSTEM ===
  const freq = paydayConfig?.frequency || "Weekly";
  let allowedPaydays = [];
  
  if (isEntrepreneurMode) {
    allowedPaydays = ["Week 1", "Week 2", "Week 3", "Week 4"];
  } else {
    if (freq === "Monthly") allowedPaydays = ["Payday 1"];
    else if (freq === "Semi-Monthly") allowedPaydays = ["Payday 1", "Payday 2"];
    else if (freq === "Bi-Weekly") allowedPaydays = ["Payday 1", "Payday 2", "Payday 3"];
    else allowedPaydays = ["Payday 1", "Payday 2", "Payday 3", "Payday 4", "Payday 5"];
  }
  
  const hzPaydays = ["Due Now", ...allowedPaydays];

  let paydayWindows = {};
  let activePaydaysWithDates = [];

  if (!isEntrepreneurMode) {
    activePaydaysWithDates = allowedPaydays
      .filter(pd => paydayConfig?.[pd]?.date)
      .map(pd => ({ key: pd, date: paydayConfig[pd].date, millis: parseLocalDate(paydayConfig[pd].date) }))
      .sort((a, b) => a.millis - b.millis);

    activePaydaysWithDates.forEach((pd, idx) => {
      const startMillis = idx === 0 ? todayMidnightMillis : paydayWindows[activePaydaysWithDates[idx - 1].key].end + 1;
      let endMillis = 0;

      if (idx < activePaydaysWithDates.length - 1) {
        endMillis = activePaydaysWithDates[idx + 1].millis - 1;
      } else {
        let lastDateObj = new Date(pd.date.split("-")[0], parseInt(pd.date.split("-")[1], 10) - 1, pd.date.split("-")[2]);
        if (freq === "Weekly") lastDateObj.setDate(lastDateObj.getDate() + 7);
        else if (freq === "Bi-Weekly") lastDateObj.setDate(lastDateObj.getDate() + 14);
        else if (freq === "Semi-Monthly") lastDateObj.setDate(lastDateObj.getDate() + 15);
        else lastDateObj.setMonth(lastDateObj.getMonth() + 1);
        
        const endOfMonthObj = new Date(currentYearIdx, currentMonthIdx + 1, 0);
        if (lastDateObj.getTime() < endOfMonthObj.getTime()) {
          lastDateObj = endOfMonthObj;
        }

        lastDateObj.setHours(23, 59, 59, 999);
        endMillis = lastDateObj.getTime();
      }

      paydayWindows[pd.key] = { start: startMillis, end: endMillis };
    });
  }

  const getBillRunwayGroup = (bill) => {
    if (bill.isOverdue || bill.payday === "Due Now") return "Due Now";
    if (!bill.rawDate) return null;
    
    if (isEntrepreneurMode) {
      const dateParts = bill.rawDate.split("-");
      const day = dateParts.length === 3 ? parseInt(dateParts[2], 10) : new Date(bill.rawDate).getDate();
      if (day <= 7) return "Week 1";
      if (day <= 14) return "Week 2";
      if (day <= 21) return "Week 3";
      return "Week 4";
    }

    const billMillis = parseLocalDate(bill.rawDate);
    for (let i = 0; i < activePaydaysWithDates.length; i++) {
      const pdKey = activePaydaysWithDates[i].key;
      const window = paydayWindows[pdKey];
      if (window && billMillis >= window.start && billMillis <= window.end) {
        return pdKey;
      }
    }

    return null;
  };

  const isCurrentMonthBill = (bill) => {
    if (bill.isOverdue || bill.payday === "Due Now") return true;
    if (!bill.rawDate) return false;
    const parts = bill.rawDate.split("-");
    if (parts.length === 3) {
      const bMonth = parseInt(parts[1], 10) - 1;
      const bYear = parseInt(parts[0], 10);
      return bMonth === currentMonthIdx && bYear === currentYearIdx;
    }
    return false;
  };

  const billsByRunwayGroup = { "Due Now": [] };
  hzPaydays.forEach(pd => { billsByRunwayGroup[pd] = []; });
  
  bills.forEach(bill => {
    let includeInGroup = true;
    if (isEntrepreneurMode) {
      includeInGroup = false;
      if (bill.isOverdue || bill.payday === "Due Now") {
        includeInGroup = true;
      } else if (bill.rawDate) {
        const parts = bill.rawDate.split("-");
        if (parts.length === 3) {
          const bMonth = parseInt(parts[1], 10) - 1;
          const bYear = parseInt(parts[0], 10);
          if (bMonth === currentMonthIdx && bYear === currentYearIdx) {
            includeInGroup = true;
          }
        }
      }
    }

    if (includeInGroup) {
      const assignedGroup = getBillRunwayGroup(bill);
      if (assignedGroup && billsByRunwayGroup[assignedGroup]) {
        billsByRunwayGroup[assignedGroup].push(bill);
      }
    }
  });

  const sortBillsSurgically = (billList) => {
    return [...billList].sort((a, b) => {
      if (a.isOverdue && !b.isOverdue) return -1;
      if (!a.isOverdue && b.isOverdue) return 1;
      if (a.payday === "Due Now" && b.payday !== "Due Now") return -1;
      if (a.payday !== "Due Now" && b.payday === "Due Now") return 1;
      return parseLocalDate(a.rawDate) - parseLocalDate(b.rawDate);
    });
  };

  const liquidAccounts = accounts.filter(a => !a.isGoal);
  const totalIncomeBalance = liquidAccounts.reduce((sum, a) => sum + (Number(a?.balance) || 0), 0);

  // === DASHBOARD FINANCIAL ENGINE ===
  const currentMonthBillsTotal = bills.reduce((sum, bill) => {
    if (bill.isPaid) return sum;
    return isCurrentMonthBill(bill) ? sum + (Number(bill.amount) || 0) : sum;
  }, 0);

  const safeToSpend = totalIncomeBalance - currentMonthBillsTotal;

  // === GAMIFIED "BILLS PAID" RING FORMULA ===
  let currentMonthTotalBillsCount = 0;
  let currentMonthSettledBillsCount = 0;

  bills.forEach((bill) => {
    if (isCurrentMonthBill(bill)) {
      currentMonthTotalBillsCount++;
      if (bill.isPaid) {
        currentMonthSettledBillsCount++;
      }
    }
  });

  const billsPaidPercentage = currentMonthTotalBillsCount === 0 
    ? 100 
    : Math.max(0, Math.min((currentMonthSettledBillsCount / currentMonthTotalBillsCount) * 100, 100));

  let runningBalance = totalIncomeBalance;
  const hzBalances = {};

  hzPaydays.forEach((pd) => {
    const groupBills = billsByRunwayGroup[pd] || [];
    const unpaidTotal = groupBills
      .filter(b => !b.isPaid && isCurrentMonthBill(b))
      .reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

    if (isEntrepreneurMode) {
      runningBalance = runningBalance - unpaidTotal;
      hzBalances[pd] = runningBalance;
    } else {
      const pdSettings = paydayConfig?.[pd] || {};
      if (pd !== "Due Now" && !pdSettings?.date) {
        hzBalances[pd] = runningBalance;
        return;
      }
      let income = 0;
      if (pd !== "Due Now") {
        income = Number(pdSettings.income) || 0;
      }
      runningBalance = runningBalance + income - unpaidTotal;
      hzBalances[pd] = runningBalance;
    }
  });

  let daysUntilNext = 0;
  let nextPaydayDayName = "";

  if (!isEntrepreneurMode && activePaydaysWithDates.length > 0) {
    const upcoming = activePaydaysWithDates.filter(pd => pd.millis >= todayMidnightMillis);
    if (upcoming.length > 0) {
      daysUntilNext = Math.ceil((upcoming[0].millis - todayMidnightMillis) / (1000 * 60 * 60 * 24));
      const parts = upcoming[0].date.split("-");
      const localDateObj = new Date(parts[0], parseInt(parts[1], 10) - 1, parts[2]);
      nextPaydayDayName = localDateObj.toLocaleDateString("en-US", { weekday: 'long' }).toUpperCase();
    }
  }

  let displayActiveAccordion = activePayday;
  if (activePayday === "init") {
    const groupUnpaid = (billsByRunwayGroup["Due Now"] || []).filter(b => !b.isPaid);
    if (groupUnpaid.length > 0) {
      displayActiveAccordion = "Due Now";
    } else {
      displayActiveAccordion = null;
    }
  }

  const getPillStyle = (amt) => {
    if (amt > 0) return isDarkMode ? "bg-emerald-500/10 border-emerald-900/50 text-[#10B981] shadow-[0_0_15px_rgba(16,185,129,0.15)]" : "bg-emerald-50 border-emerald-200 text-[#10B981] shadow-[0_0_15px_rgba(16,185,129,0.2)]";
    if (amt < 0) return isDarkMode ? "bg-red-500/10 border-red-900/50 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.15)]" : "bg-red-50 border-red-200 text-red-500 shadow-[0_0_15px_rgba(239,68,68,0.2)]";
    return isDarkMode ? "bg-[#1E293B] border-slate-700 text-white shadow-sm" : "bg-white border-slate-100 text-slate-900 shadow-sm";
  };

  const getDotClass = (amt) => {
    let base = "animate-pulse shrink-0 w-2 h-2 rounded-full ";
    if (amt > 0) return base + "bg-[#10B981]";
    if (amt < 0) return base + "bg-red-500";
    return base + (isDarkMode ? "bg-white" : "bg-slate-900");
  };

  const currentGaugeValue = gaugePhase === "start" ? 0 : gaugePhase === "sweep" ? 100 : billsPaidPercentage;

  const graphicContent = (
    <div className="flex flex-col relative z-10 mb-2 w-full">
      <div className={`relative pt-10 pb-6 px-6 rounded-[2rem] border flex flex-col items-center w-full transform transition-all duration-700 ease-out ${isMounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"} ${isDarkMode ? "bg-gradient-to-br from-blue-900/60 via-slate-800 via-25% to-slate-800 border-slate-700/50 border-t-slate-600/40 shadow-[0_12px_30px_rgba(0,0,0,0.5)]" : "bg-gradient-to-br from-blue-600/20 via-white via-25% to-slate-50 border-slate-200/60 border-t-white shadow-[inset_0_2px_3px_rgba(255,255,255,1),0_12px_24px_rgba(24,119,242,0.15),0_4px_12px_rgba(0,0,0,0.01)]"}`}>
          
        <div className="absolute top-4 w-full flex justify-center pointer-events-none">
          <span className={`text-[10px] font-black uppercase tracking-widest opacity-80 ${isDarkMode ? "text-white" : "text-black"}`}>
            {currentMonthName}'s Monthly Snapshot
          </span>
        </div>

        {/* 1. Tactical Radial Gauge */}
        <div className={`relative w-40 h-40 flex-shrink-0 mt-6 mb-1 transform transition-all duration-700 delay-100 ease-out ${isMounted ? "scale-100 opacity-100" : "scale-90 opacity-0"}`}>
          <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible">
            <g transform="translate(50, 50)">
              {[...Array(24)].map((_, i) => {
                const angle = -135 + (i * (270 / 23));
                const isActive = (i / 23) * 100 <= currentGaugeValue;
                return (
                  <line
                    key={i}
                    x1="0"
                    y1="-36"
                    x2="0"
                    y2="-46"
                    stroke={isActive ? "#1877F2" : (isDarkMode ? "#334155" : "#E2E8F0")}
                    strokeWidth="4"
                    strokeLinecap="round"
                    transform={`rotate(${angle})`}
                    className={`transition-colors duration-500 ease-out ${isActive ? "drop-shadow-[0_0_6px_rgba(24,119,242,0.8)]" : ""}`}
                    style={{ transitionDelay: gaugePhase === "sweep" ? `${i * 15}ms` : `${(23 - i) * 10}ms` }}
                  />
                );
              })}
            </g>
          </svg>
          
          {/* "X/Y" Centered Fractional Output */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none scale-[0.9]">
            <span className={`text-4xl font-black tracking-tight leading-none ${isDarkMode ? "text-white" : "text-slate-900"}`}>
              {currentMonthSettledBillsCount}/{currentMonthTotalBillsCount}
            </span>
          </div>
        </div>

        <div className="-mt-5 mb-3 text-center pointer-events-none">
          <span className="text-sm font-black uppercase tracking-widest text-[#1877F2]">
            BILLS PAID IN {currentMonthName.toUpperCase()}
          </span>
        </div>
        
        <div className="w-full space-y-2 mt-1">
          {/* 1. MY BALANCE PILL */}
          <div 
            className={`w-full py-3 px-4 rounded-xl border flex items-center justify-between transform transition-all duration-[600ms] delay-[200ms] ${isMounted ? "translate-x-0 opacity-100" : "-translate-x-12 opacity-0"} ${getPillStyle(totalIncomeBalance)}`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <div className="flex items-center gap-2">
              <div className={getDotClass(totalIncomeBalance)}></div>
              <span className="text-[10px] font-black uppercase tracking-widest">
                My Balance
              </span>
            </div>
            <span className="text-xl font-black leading-none">
              {totalIncomeBalance < 0 ? `-${currencySymbol}` : currencySymbol}{Math.abs(totalIncomeBalance).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {/* 2. DUE TOMORROW PILL (CONDITIONALLY RENDERED ONLY WHEN > $0.00) */}
          {dueTomorrowTotal > 0 && (
            <div 
              className={`w-full py-3 px-4 rounded-xl border flex items-center justify-between transform transition-all duration-[600ms] delay-[250ms] ${isMounted ? "translate-x-0 opacity-100" : "-translate-x-12 opacity-0"} ${isDarkMode ? "bg-blue-900/20 border-blue-500/40 text-[#1877F2] shadow-[0_0_15px_rgba(24,119,242,0.15)]" : "bg-blue-50 border-blue-200 text-[#1877F2] shadow-[0_0_15px_rgba(24,119,242,0.12)]"}`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <div className="flex items-center gap-2">
                <div className="animate-pulse shrink-0 w-2 h-2 rounded-full bg-[#1877F2]"></div>
                <span className="text-[10px] font-black uppercase tracking-widest">
                  Due Tomorrow
                </span>
              </div>
              <span className="text-xl font-black leading-none">
                {currencySymbol}{dueTomorrowTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}

          {/* 3. SAFE TO SPEND PILL */}
          <div 
            className={`w-full py-3 px-4 rounded-xl border flex items-center justify-between transform transition-all duration-[600ms] delay-[300ms] ${isMounted ? "translate-x-0 opacity-100" : "translate-x-12 opacity-0"} ${getPillStyle(safeToSpend)}`}
            style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
          >
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest">
                Safe to Spend
              </span>
            </div>
            <span className="text-xl font-black leading-none">
              {safeToSpend < 0 ? `-${currencySymbol}` : currencySymbol}{Math.abs(safeToSpend).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>

          {!isEntrepreneurMode && nextPaydayDayName && (
            <div 
              className={`w-full pt-3 flex items-center justify-center transform transition-all duration-[600ms] delay-[400ms] ${isMounted ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <span className="text-[10px] font-black uppercase tracking-widest text-[#10B981] leading-none">
                NEXT PAYDAY: {nextPaydayDayName}{" "}
                <span className="opacity-80">
                  {daysUntilNext === 0 && "(TODAY)"}
                  {daysUntilNext === 1 && "(IN 1 DAY)"}
                  {daysUntilNext > 1 && `(IN ${daysUntilNext} DAYS)`}
                </span>
              </span>
            </div>
          )}

        </div>
      </div>
    </div>
  );

  return (
    <div className={`pb-32 transition-colors duration-500 min-h-screen relative overflow-hidden ${isDarkMode ? "bg-[#0F172A]" : "bg-[#F8FAFC]"}`}>
      
      <div className="relative z-10 Dashboard-Master-Header">
        <style>{`
          .Dashboard-Master-Header h1, 
          .Dashboard-Master-Header h2,
          .Dashboard-Master-Header h3 { 
            color: ${isDarkMode ? "#FFFFFF" : "#000000"} !important; 
            font-weight: 900 !important;
          }
        `}</style>
        {renderHeroShell(greetingStr, graphicContent)}
      </div>

      <div className={`mx-6 mb-5 border-t relative z-10 ${isDarkMode ? "border-[#FFFFFF]" : "border-slate-300"}`}></div>
      
      <div className="px-6 relative z-10">
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 px-2 mb-4">
          {isEntrepreneurMode ? `${currentMonthName}'s Projected Weekly Runway` : `${currentMonthName}'s Projected Pay Schedule`}
        </h3>
        
        <div className="flex justify-center mb-5">
          {isEntrepreneurMode ? (
            <button className={`w-full max-w-sm py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 border transition-all ${isDarkMode ? "bg-[#1E293B] border-slate-700 text-[#1877F2] shadow-sm" : "bg-white/80 backdrop-blur-md border-slate-200 text-[#1877F2] shadow-[0_4px_20px_rgba(0,0,0,0.03)]"}`}>
              🚀 Entrepreneur Mode Active
            </button>
          ) : (
            <button onClick={() => setIsPaydaySetupOpen(true)} className={`w-full max-w-sm py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 border transition-all active:scale-95 ${isDarkMode ? "bg-[#1E293B] border-slate-700 text-[#1877F2] shadow-sm" : "bg-white/80 backdrop-blur-md border-slate-200 text-[#1877F2] shadow-[0_4px_20px_rgba(0,0,0,0.03)]"}`}>
              <Settings2 size={18} strokeWidth={2.5} /> Set {currentMonthName}'s Pay Dates & Amounts
            </button>
          )}
        </div>
      </div>

      <div className="w-full overflow-x-auto hide-scrollbar pl-6 pr-6 mb-6 relative z-10">
        <div className="flex gap-4 pr-6 pb-2 min-h-[170px]">
          {hzPaydays.map((pd) => {
            const pdSettings = paydayConfig?.[pd] || {};
            const groupBills = billsByRunwayGroup[pd] || [];
            
            if (!isEntrepreneurMode && pd !== "Due Now" && !pdSettings?.date) return null;

            const unpaidBills = groupBills.filter(b => !b.isPaid);
            const currentMonthUnpaidBills = unpaidBills.filter(b => isCurrentMonthBill(b));
            const unpaidCount = unpaidBills.length;
            const currentMonthUnpaidTotal = currentMonthUnpaidBills.reduce((sum, b) => sum + (Number(b.amount) || 0), 0);

            if (pd === "Due Now" && unpaidCount === 0) return null;

            let totalExpectedIncome = 0;
            let expectedDateStr = "";

            if (isEntrepreneurMode) {
              if (pd === "Due Now") expectedDateStr = "ACTION REQ";
              else if (pd === "Week 1") expectedDateStr = "DAYS 1-7";
              else if (pd === "Week 2") expectedDateStr = "DAYS 8-14";
              else if (pd === "Week 3") expectedDateStr = "DAYS 15-21";
              else if (pd === "Week 4") expectedDateStr = `Days 22-${lastDayOfMonth}`;
            } else {
              totalExpectedIncome = Number(pdSettings.income) || 0;
              expectedDateStr = pd === "Due Now" ? "ACTION REQ" : formatPaydayDateStr(pdSettings.date).toUpperCase();
            }

            const waterfallBalance = hzBalances[pd];
            const isDeficit = waterfallBalance < 0;
            const subLabelStr = pd === "Due Now" ? "AVAILABLE NOW" : "AVAILABLE THIS WEEK";

            return (
              <div 
                key={`hz-${pd}`} 
                onClick={() => { 
                  setActivePayday(pd); 
                  const targetEl = document.getElementById(`vert-${pd}`);
                  if (targetEl) targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }} 
                className={`shrink-0 w-52 p-5 rounded-[1.75rem] border cursor-pointer active:scale-95 transition-all flex flex-col justify-between h-44 ${pd === "Due Now" ? (isDarkMode ? "bg-red-900/10 border-red-900/40 shadow-md" : "bg-red-50 border-red-100 shadow-[0_4px_20px_rgba(239,68,68,0.1)]") : (isDarkMode ? "bg-[#1E293B] border-slate-700 shadow-md" : "bg-white/90 backdrop-blur-sm border-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.04)]")}`}
              >
                
                <div className="flex justify-between items-center w-full">
                  <h4 className={`text-[10px] font-black uppercase tracking-widest ${pd === "Due Now" ? "text-red-500" : "text-slate-400"}`}>{pd}</h4>
                  <span className={`text-[9px] font-black uppercase tracking-widest ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>{expectedDateStr}</span>
                </div>

                <div className="text-center pt-1.5 pb-1">
                   <p className={`text-2xl font-black tracking-tighter leading-none mb-1 ${isDeficit ? "text-red-500" : "text-[#10B981]"}`}>
                    {isDeficit ? `-${currencySymbol}` : currencySymbol}{Math.abs(waterfallBalance).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </p>
                  <span className={`text-[8px] font-black uppercase tracking-[0.15em] ${isDarkMode ? "text-white" : "text-black"} leading-none block`}>
                    {subLabelStr}
                  </span>
                </div>

                <div className="w-full py-1.5 rounded-xl text-center font-black text-[9px] tracking-wider transition-all uppercase bg-[#1877F2] text-white shadow-md">
                  VIEW DETAILS
                </div>

                <div className="flex justify-between items-end w-full pt-2">
                  {pd === "Due Now" ? (
                    <div className="flex flex-col flex-1"></div> 
                  ) : (
                    <div className="flex flex-col flex-1">
                      {isEntrepreneurMode ? (
                        <>
                          <span className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isDarkMode ? "text-white" : "text-black"}`}>Income</span>
                          <span className={`text-[10px] font-black ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>VARIABLE</span>
                        </>
                      ) : (
                        <>
                          <span className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isDarkMode ? "text-white" : "text-black"}`}>Expected Pay</span>
                          <span className={`text-[10px] font-black ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`}>+{currencySymbol}{totalExpectedIncome.toLocaleString("en-US", { minimumFractionDigits: 0 })}</span>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex flex-col items-end shrink-0">
                     <span className={`text-[7px] font-black uppercase tracking-widest mb-0.5 ${isDarkMode ? "text-white" : "text-black"}`}>
                      {unpaidCount === 1 ? `${unpaidCount} Bill Out` : `${unpaidCount} Bills Out`}
                    </span>
                    <span className="text-[10px] font-black text-[#1877F2]">-{currencySymbol}{currentMonthUnpaidTotal.toLocaleString("en-US", { minimumFractionDigits: 0 })}</span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      </div>

      <div className={`mx-6 mb-6 border-t relative z-10 ${isDarkMode ? "border-[#FFFFFF]" : "border-slate-300"}`}></div>

      <main className="px-6 space-y-4 relative z-10">
        
        <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 px-2 mb-4">
          {isEntrepreneurMode ? `${currentMonthName}'s Weekly Runway` : `${currentMonthName}'s Pay Schedule`}
        </h3>

        <div className="space-y-4">
          {["Due Now", ...allowedPaydays].map((payday) => {
            const groupBills = billsByRunwayGroup[payday] || [];
            const activeGroupBills = groupBills.filter(b => !b.isPaid); 
            const isDueNow = payday === "Due Now";
            
            if (!isEntrepreneurMode) {
              const pdSettings = paydayConfig?.[payday] || {};
              if (isDueNow && activeGroupBills.length === 0) return null;
              if (!isDueNow && !pdSettings?.date) return null;
            } else {
              if (activeGroupBills.length === 0) return null;
            }

            const isCollapsed = displayActiveAccordion !== payday;
            
            // Strictly total bills belonging to the current month for header accuracy
            const checkTotal = activeGroupBills
              .filter(b => isCurrentMonthBill(b))
              .reduce((sum, b) => sum + (Number(b?.amount) || 0), 0);
              
            const sortedBills = sortBillsSurgically(activeGroupBills);

            let expectedDateStr = "";
            if (isEntrepreneurMode) {
              if (isDueNow) expectedDateStr = "Currently Due";
              else if (payday === "Week 1") expectedDateStr = "Days 1-7";
              else if (payday === "Week 2") expectedDateStr = "Days 8-14";
              else if (payday === "Week 3") expectedDateStr = "Days 15-21";
              else if (payday === "Week 4") expectedDateStr = `Days 22-${lastDayOfMonth}`;
            } else {
              const pdSettings = paydayConfig?.[payday] || {};
              expectedDateStr = isDueNow ? "Currently Due" : formatPaydayDateStr(pdSettings.date);
            }

            return (
              <div key={payday} id={`vert-${payday}`} className="space-y-2 scroll-mt-24">
                <div className="flex flex-col px-3 py-3 cursor-pointer" onClick={() => setActivePayday(isCollapsed ? payday : null)}>
                  <div className="flex items-center justify-between w-full mb-1">
                     <div className="flex items-center gap-2">
                       <h3 className={`text-sm font-black uppercase tracking-widest ${isDueNow ? "text-red-500" : isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{payday}</h3>
                       <div className="text-slate-400">{isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}</div>
                     </div>
                     <span className={`text-sm font-black ${isDueNow ? "text-red-500" : "text-[#1877F2]"}`}>{currencySymbol}{checkTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between w-full">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{expectedDateStr}</span>
                    <span className={`text-[9px] font-bold uppercase tracking-widest ${isDueNow ? "text-red-500/80" : "text-slate-400"}`}>Total Due</span>
                  </div>
                </div>

                {!isCollapsed && (
                  <div className={`rounded-[2rem] p-4 border transition-all ${isDueNow ? (isDarkMode ? "bg-red-900/10 border-red-900/30" : "bg-red-50/30 border-red-100") : (isDarkMode ? "bg-[#1E293B] border-slate-800" : "bg-white/80 backdrop-blur-md border-white/60 shadow-sm")}`}>
                    <div className="space-y-3">
                      {sortedBills.length === 0 ? (
                        <p className="text-center py-4 text-xs font-bold text-slate-400">All caught up!</p>
                      ) : (
                        sortedBills.map((bill) => {
                          const billDateObj = bill.rawDate ? new Date(parseLocalDate(bill.rawDate)) : null;
                          const isNextMonthBill = billDateObj && (billDateObj.getMonth() !== currentMonthIdx || billDateObj.getFullYear() !== currentYearIdx);

                          return (
                            <div key={bill?.id} className={`flex flex-col p-4 rounded-2xl border shadow-sm transition-colors ${isDarkMode ? "bg-slate-800/50 border-slate-700" : "bg-white border-slate-100 hover:bg-slate-50"}`}>
                              
                              {/* LEVEL 1: Left Emoji & Name, Right Edit Pencil */}
                              <div className="flex items-start justify-between w-full mb-4">
                                 <div className="flex items-center gap-3 cursor-pointer flex-1 min-w-0" onClick={() => setSelectedEntry(bill)}>
                                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center text-xl shrink-0 ${isDarkMode ? "bg-slate-900/50 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
                                      {bill?.icon || "🧾"}
                                    </div>
                                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                                      <p className={`font-black text-base truncate leading-tight ${isDarkMode ? "text-white" : "text-slate-900"}`}>{bill?.name || "Unnamed"}</p>
                                      {bill?.isRecurring && (
                                        <RefreshCw size={12} strokeWidth={2.5} className="text-[#10B981] shrink-0" />
                                      )}
                                    </div>
                                 </div>
                                 <button 
                                    onClick={(e) => { e.stopPropagation(); setSelectedEntry(bill); }}
                                    className={`p-2 shrink-0 rounded-full transition-all active:scale-95 ${isDarkMode ? "hover:bg-slate-700 text-slate-500 hover:text-slate-300" : "hover:bg-slate-100 text-slate-400 hover:text-slate-600"}`}
                                 >
                                    <Edit2 size={16} strokeWidth={2.5} />
                                 </button>
                              </div>

                              {/* DYNAMIC 3-LEVEL LAYOUT: Standard vs. Installment */}
                              {bill?.isInstallment ? (
                                <>
                                  {/* INSTALLMENT LEVEL 2: Left Status/Date, Right Amount */}
                                  <div className="flex items-center justify-between w-full">
                                     <div className="flex flex-col shrink-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className={`text-[10px] min-[360px]:text-xs font-black uppercase tracking-wider ${(bill?.isOverdue || bill?.payday === "Due Now") ? "text-red-500" : "text-slate-400"}`}>
                                             {bill?.isOverdue ? "Overdue" : bill?.payday === "Due Now" ? "Due Now" : "Due"}
                                          </span>
                                          {isNextMonthBill && !bill?.isOverdue && bill?.payday !== "Due Now" && (
                                            <span className="text-[10px] min-[360px]:text-xs font-black uppercase tracking-wider text-[#1877F2]">
                                               NEXT MONTH
                                            </span>
                                          )}
                                        </div>
                                        <span className={`text-[10px] min-[360px]:text-xs font-bold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                           {bill?.fullDate || "TBD"}
                                        </span>
                                     </div>
                                     
                                     <div className={`px-2 min-[360px]:px-2.5 py-1 rounded-[8px] border font-black text-sm min-[360px]:text-base tracking-tighter shrink-0 text-[#1877F2] ${isDarkMode ? "bg-blue-900/20 border-blue-500/30" : "bg-blue-50 border-blue-200"} drop-shadow-[0_0_12px_rgba(24,119,242,0.7)] whitespace-nowrap`}>
                                        {currencySymbol}{(Number(bill?.amount) || 0).toFixed(2)}
                                     </div>
                                  </div>

                                  <div className={`my-4 border-t ${isDarkMode ? "border-slate-700/50" : "border-slate-200"}`}></div>

                                  {/* INSTALLMENT LEVEL 3: Centered Button */}
                                  <div className="flex items-center justify-center w-full mb-4">
                                      {!bill?.isPaid ? (
                                          <button onClick={(e) => { e.stopPropagation(); handleBillClick(bill?.id); }} className="w-full max-w-[200px] px-3 min-[360px]:px-5 py-2 min-[360px]:py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest bg-[#1877F2] text-white shadow-lg active:scale-95 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap" >
                                            <CheckCircle2 size={14} strokeWidth={2.5} />
                                            MARK AS PAID
                                          </button>
                                      ) : (
                                          <div className="w-full max-w-[200px] px-3 min-[360px]:px-5 py-2 min-[360px]:py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center gap-1.5 whitespace-nowrap"><CheckCircle2 size={14} strokeWidth={2.5} /> Paid</div>
                                      )}
                                  </div>

                                  {/* INSTALLMENT LEVEL 4: Progress Bar */}
                                  <div className="w-full">
                                       <div className="flex justify-between items-end mb-2">
                                           <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Installment Plan</span>
                                           <span className="text-xs sm:text-sm font-black text-slate-600 dark:text-slate-300">
                                               {currencySymbol}{(Number(bill?.paidAmount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })} / {currencySymbol}{(Number(bill?.totalAmount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                           </span>
                                       </div>
                                       <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDarkMode ? "bg-slate-900 shadow-inner" : "bg-slate-100"}`}>
                                           <div className="h-full bg-[#1877F2] transition-all duration-1000" style={{ width: `${Math.min(((Number(bill?.paidAmount) || 0) / (Number(bill?.totalAmount) || 1)) * 100, 100)}%` }}></div>
                                       </div>
                                   </div>
                                </>
                              ) : (
                                <>
                                  {/* STANDARD LEVEL 2: Left Status/Date, Right Amount */}
                                  <div className="flex items-center justify-between w-full">
                                     <div className="flex flex-col shrink-0">
                                        <div className="flex items-center gap-1.5">
                                          <span className={`text-[10px] min-[360px]:text-xs font-black uppercase tracking-wider ${(bill?.isOverdue || bill?.payday === "Due Now") ? "text-red-500" : "text-slate-400"}`}>
                                             {bill?.isOverdue ? "Overdue" : bill?.payday === "Due Now" ? "Due Now" : "Due"}
                                          </span>
                                          {isNextMonthBill && !bill?.isOverdue && bill?.payday !== "Due Now" && (
                                            <span className="text-[10px] min-[360px]:text-xs font-black uppercase tracking-wider text-[#1877F2]">
                                               NEXT MONTH
                                            </span>
                                          )}
                                        </div>
                                        <span className={`text-[10px] min-[360px]:text-xs font-bold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                           {bill?.fullDate || "TBD"}
                                        </span>
                                     </div>
                                     
                                     <div className={`px-2 min-[360px]:px-2.5 py-1 rounded-[8px] border font-black text-sm min-[360px]:text-base tracking-tighter shrink-0 text-[#1877F2] drop-shadow-[0_0_12px_rgba(24,119,242,0.7)] ${isDarkMode ? "bg-blue-900/20 border-blue-500/30" : "bg-blue-50 border-blue-200"} whitespace-nowrap`}>
                                        {currencySymbol}{(Number(bill?.amount) || 0).toFixed(2)}
                                     </div>
                                  </div>

                                  <div className={`my-4 border-t ${isDarkMode ? "border-slate-700/50" : "border-slate-200"}`}></div>

                                  {/* STANDARD LEVEL 3: Centered Button */}
                                  <div className="flex items-center justify-center w-full">
                                      {!bill?.isPaid ? (
                                          <button onClick={(e) => { e.stopPropagation(); handleBillClick(bill?.id); }} className="w-full max-w-[200px] px-3 min-[360px]:px-5 py-2 min-[360px]:py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest bg-[#1877F2] text-white shadow-lg active:scale-95 transition-all flex items-center justify-center gap-1.5 whitespace-nowrap" >
                                            <CheckCircle2 size={14} strokeWidth={2.5} />
                                            MARK AS PAID
                                          </button>
                                      ) : (
                                          <div className="w-full max-w-[200px] px-3 min-[360px]:px-5 py-2 min-[360px]:py-2.5 rounded-xl font-black text-[10px] uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center gap-1.5 whitespace-nowrap"><CheckCircle2 size={14} strokeWidth={2.5} /> Paid</div>
                                      )}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={`mt-6 py-4 px-5 rounded-[1.5rem] border flex flex-col items-center justify-center gap-2 transition-all ${isDarkMode ? "bg-[#1E293B] border-slate-800 shadow-sm" : "bg-white/80 backdrop-blur-md border-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"}`}>
           <span className={`text-[11px] font-black uppercase tracking-widest ${isDarkMode ? "text-white" : "text-slate-900"}`}>Total Bills for {currentMonthName}</span>
           <div className={`px-3 py-1.5 rounded-[8px] border font-black text-lg tracking-tighter shrink-0 text-[#1877F2] drop-shadow-[0_0_12px_rgba(24,119,242,0.7)] ${isDarkMode ? "bg-blue-900/20 border-blue-500/30" : "bg-blue-50 border-blue-200"}`}>
             {currencySymbol}{currentMonthBillsTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
           </div>
        </div>

        <div className={`space-y-4 pt-4 border-t mt-8 ${isDarkMode ? "border-[#FFFFFF]" : "border-slate-300"}`}>
          <h3 className="text-xs font-black uppercase tracking-widest text-slate-400 px-2">Recent Activity</h3>
          <div className={`rounded-[2rem] p-4 border shadow-sm transition-all ${isDarkMode ? "bg-[#1E293B] border-slate-800" : "bg-white/80 backdrop-blur-md border-white/60 shadow-sm"}`}>
            {transactions.length === 0 ? ( <p className="text-center py-8 font-bold text-slate-400">No activity yet.</p> ) : (
              <div className="space-y-3">
                {transactions.slice(0, 5).map((tx) => {
                  const isBillTx = tx?.isBillPayment || tx?.type === "Bill" || (tx?.category && tx?.category.toLowerCase().includes("bill"));
                  let txColorStr = "";
                  let txBgBorderStr = "";
                  let txShadowStr = "";
                  let txPrefix = "";

                  if (tx?.type === "Income") {
                    txColorStr = "text-emerald-500";
                    txBgBorderStr = isDarkMode ? "bg-emerald-900/20 border-emerald-500/30" : "bg-emerald-50 border-emerald-200";
                    txShadowStr = "drop-shadow-[0_0_12px_rgba(16,185,129,0.7)]";
                    txPrefix = "+";
                  } else if (isBillTx) {
                    txColorStr = "text-[#1877F2]";
                    txBgBorderStr = isDarkMode ? "bg-blue-900/20 border-blue-500/30" : "bg-blue-50 border-blue-200";
                    txShadowStr = "drop-shadow-[0_0_12px_rgba(24,119,242,0.7)]";
                    txPrefix = "-";
                  } else {
                    txColorStr = "text-orange-500";
                    txBgBorderStr = isDarkMode ? "bg-orange-900/20 border-orange-500/30" : "bg-orange-50 border-orange-200";
                    txShadowStr = "drop-shadow-[0_0_12px_rgba(249,115,22,0.7)]";
                    txPrefix = "-";
                  }

                  return (
                    <div key={tx?.id} className={`flex flex-col p-4 rounded-[1.5rem] border transition-all ${isDarkMode ? "bg-slate-800/50 border-slate-700 hover:bg-slate-800" : "bg-white border-slate-100 hover:bg-slate-50 shadow-sm"}`}>
                      
                      <div className="flex items-start justify-between w-full mb-3 gap-2">
                         <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`w-12 h-12 rounded-xl border flex items-center justify-center text-2xl shrink-0 ${isDarkMode ? "bg-slate-900/50 border-slate-700" : "bg-slate-50 border-slate-200"}`}>
                               {tx?.icon || "💳"}
                            </div>
                            <div className="flex flex-col flex-1 min-w-0 pt-1">
                               <p className={`font-black text-base leading-tight break-words whitespace-normal ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                                  {tx?.name || "Transaction"}
                               </p>
                            </div>
                         </div>
                         <button 
                            onClick={(e) => { e.stopPropagation(); setSelectedEntry(tx); }} 
                            className={`p-2 shrink-0 rounded-full transition-all active:scale-95 ${isDarkMode ? "hover:bg-slate-700 text-slate-500 hover:text-slate-300" : "hover:bg-slate-100 text-slate-400 hover:text-slate-600"}`}
                         >
                            <Edit2 size={16} strokeWidth={2.5} />
                         </button>
                      </div>

                      <div className={`w-full border-t mb-3 ${isDarkMode ? "border-slate-700/50" : "border-slate-100"}`}></div>

                      <div className="flex items-center justify-between gap-2 w-full">
                         <div className="flex flex-col flex-1 min-w-0 pr-2">
                          <span className={`text-[9px] font-black uppercase tracking-widest truncate w-full ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                            {tx?.isDirectGoalEntry ? "🔒 SAVED TO GOAL" : (tx?.category || "General")}
                          </span>
                          <span className={`text-[10px] font-semibold uppercase tracking-widest mt-0.5 truncate w-full ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            {tx?.date || "Recent"}
                          </span>
                         </div>
                         <div className={`px-2.5 py-1 rounded-[8px] border font-black text-base tracking-tighter shrink-0 ${txColorStr} ${txBgBorderStr} ${txShadowStr} whitespace-nowrap`}>
                            {txPrefix}{currencySymbol}{(Number(tx?.amount) || 0).toFixed(2)}
                         </div>
                      </div>

                    </div>
                  );
                })}
                
                <button onClick={() => changeTab("activity")} className="w-full mt-4 py-4 rounded-2xl font-black text-xs uppercase tracking-widest text-white bg-[#1877F2] shadow-[0_8px_16px_rgba(24,119,242,0.3)] flex items-center justify-center gap-2 transition-all active:scale-95 hover:-translate-y-0.5">
                  <List size={16} /> See All Recent Activity
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
